import type { ExcursionRules } from "@/components/RegistrationProcess/types/configurator";

/**
 * Minimal shape of a session_event_selection_rule needed to categorize excursions.
 * Kept structural so both the typed registration flow and the plain-JS experience
 * page can pass their session rules without coupling to a specific generated type.
 */
export interface ExcursionSelectionRule {
  conjunction?: string | null;
  default_event_id?: string | null;
  library_events?: ({ event_id?: string | null } | null)[] | null;
}

/**
 * Derives the excursion selection-rule sets ({@link ExcursionRules}) from a session's
 * `session_event_selection_rules`. This is the single source of truth for how excursions
 * are categorized, mirroring the registration excursions screen (see `useExcursions.ts`):
 *
 * - `and` rules contribute every event to `andIds` (all required, auto-included).
 * - `or` rules contribute every event to `orIds` (optional choices).
 * - an `or` rule with a `default_event_id` also contributes that id to `defaultIds`.
 */
export const deriveExcursionRules = (rules: readonly ExcursionSelectionRule[] = []): ExcursionRules => {
  const andIds: string[] = [];
  const orIds: string[] = [];
  const defaultIds: string[] = [];

  for (const rule of rules) {
    const ids = (rule?.library_events ?? []).map((item) => item?.event_id).filter((id): id is string => Boolean(id));

    if (rule?.conjunction === "and") andIds.push(...ids);
    if (rule?.conjunction === "or") orIds.push(...ids);
    if (rule?.conjunction === "or" && rule?.default_event_id) defaultIds.push(rule.default_event_id);
  }

  return { andIds, orIds, defaultIds };
};

/** An excursion is included (auto-selected, not removable) when it is a default or part of an AND rule. */
export const isExcursionIncluded = (rules: ExcursionRules, id: string | undefined): boolean =>
  id != null && [...rules.defaultIds, ...rules.andIds].includes(id);

/**
 * Restricts a list of excursions to those referenced by a selection rule — the members of
 * an AND or OR rule — de-duplicated by id and ordered AND-first, then OR.
 *
 * This is the visibility gate the legacy (flag-off) excursions screen applies before it
 * categorizes (see `ruleFilteredEvents` in `ExcursionLegacy/ExcursionsStep.vue`): an excursion
 * not named by any rule is never shown. The flag-on configurator applies the same filter so
 * both screens display an identical set, which is then categorized with {@link isExcursionIncluded}.
 */
export const filterExcursionsByRules = <T extends { id?: string | null }>(
  excursions: readonly T[],
  rules: ExcursionRules,
): T[] => {
  const and = excursions.filter((excursion) => excursion.id != null && rules.andIds.includes(excursion.id));
  const or = excursions.filter((excursion) => excursion.id != null && rules.orIds.includes(excursion.id));
  const seen = new Set<string>();
  return [...and, ...or].filter((excursion) => {
    const id = excursion.id as string;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};
